import type { ApiResponse } from '@/types/auth';
import { api } from '@/api/client';

export type AdminUserListItem = {
  id: string;
  email: string;
  is_active: boolean;
  is_demo: boolean;
  global_roles: string[];
  profile_grant_count: number | null;
  created_at: string | null;
};

export type ProfileGrant = {
  profile_id: string;
  role_code: string;
  account_name: string | null;
  manager_account_name: string | null;
  country_code: string | null;
  granted_at: string | null;
  granted_by: string | null;
};

export type FlipkartGrant = {
  credential_id: string;
  ad_account_id: string;
  role_code: string;
  account_name: string | null;
  manager_account_name: string | null;
  granted_at: string | null;
  granted_by: string | null;
};

export type BrandGrant = {
  brand_id: string;
  role_code: string;
  account_name: string | null;
  granted_at: string | null;
  granted_by: string | null;
};

export type AdminUserDetail = {
  id: string;
  email: string;
  is_active: boolean;
  is_demo: boolean;
  global_roles: string[];
  profile_grants: ProfileGrant[];
  flipkart_grants: FlipkartGrant[];
  brand_grants: BrandGrant[];
  created_at: string | null;
};

export type AssignableAmazonAccount = {
  platform: 'amazon';
  profile_id: string;
  account_name: string | null;
  manager_account_name: string | null;
};

export type AssignableFlipkartAccount = {
  platform: 'flipkart';
  credential_id: string;
  ad_account_id: string;
  account_name: string | null;
  manager_account_name: string | null;
};

export type AssignableBrandAccount = {
  platform: 'brand';
  brand_id: string;
  account_name: string | null;
};

export type AssignableAccount =
  | AssignableAmazonAccount
  | AssignableFlipkartAccount
  | AssignableBrandAccount;

export type AssignableProfile = AssignableAccount;

export function amazonAccountKey(profileId: string): string {
  return `amazon:${profileId}`;
}

export function flipkartAccountKey(credentialId: string, adAccountId: string): string {
  return `flipkart:${credentialId}:${adAccountId}`;
}

export function brandAccountKey(brandId: string): string {
  return `brand:${brandId}`;
}

export function assignableAccountKey(account: AssignableAccount): string {
  if (account.platform === 'flipkart') {
    return flipkartAccountKey(account.credential_id, account.ad_account_id);
  }
  if (account.platform === 'brand') {
    return brandAccountKey(account.brand_id);
  }
  return amazonAccountKey(account.profile_id);
}

export function splitSelectedAccountKeys(selected: Iterable<string>): {
  profileIds: string[];
  flipkartAccounts: { credential_id: string; ad_account_id: string }[];
  brandIds: string[];
} {
  const profileIds: string[] = [];
  const flipkartAccounts: { credential_id: string; ad_account_id: string }[] = [];
  const brandIds: string[] = [];
  for (const key of selected) {
    if (key.startsWith('amazon:')) {
      const profileId = key.slice('amazon:'.length);
      if (profileId) profileIds.push(profileId);
      continue;
    }
    if (key.startsWith('flipkart:')) {
      const rest = key.slice('flipkart:'.length);
      const sep = rest.indexOf(':');
      if (sep <= 0) continue;
      const credentialId = rest.slice(0, sep);
      const adAccountId = rest.slice(sep + 1);
      if (credentialId && adAccountId) {
        flipkartAccounts.push({ credential_id: credentialId, ad_account_id: adAccountId });
      }
      continue;
    }
    if (key.startsWith('brand:')) {
      const brandId = key.slice('brand:'.length);
      if (brandId) brandIds.push(brandId);
    }
  }
  return { profileIds, flipkartAccounts, brandIds };
}

export function accountAccessLabel(account: AssignableAccount): string {
  if (account.platform === 'flipkart') {
    return account.account_name ?? account.ad_account_id;
  }
  if (account.platform === 'brand') {
    return account.account_name ?? account.brand_id;
  }
  return account.account_name ?? account.profile_id;
}

export function accountAccessSubLabel(account: AssignableAccount): string {
  if (account.platform === 'flipkart') {
    const parent = account.manager_account_name;
    return parent ? `${parent} · ${account.ad_account_id}` : account.ad_account_id;
  }
  if (account.platform === 'brand') {
    return account.brand_id;
  }
  return account.profile_id;
}

export type AdminAccountMetricRow = {
  profile_id: string;
  account_name: string | null;
  manager_account_name: string | null;
  country_code: string | null;
  currency_code: string | null;
  marketplace_id: string | null;
  cost: number;
  sales_30d: number;
};

export type AdminAccountMetricsResponse = {
  month: string;
  range: { startDate: string; endDate: string };
  items: AdminAccountMetricRow[];
  totals: { cost: number; sales_30d: number };
};

export type ReportCoverageRange = 'lookback' | 'freshness' | 'custom';

export type ReportCoverageParams = {
  range?: Exclude<ReportCoverageRange, 'custom'>;
  startDate?: string;
  endDate?: string;
};

export type ReportCoverageEnqueueBody = ReportCoverageParams & {
  query_id?: string;
  window_start?: string;
  window_end?: string;
  stages?: Array<'failed' | 'not_scheduled' | 'scheduled'>;
};

export type ReportCoverageEnqueueResult = {
  profile_id?: string;
  credential_id?: string;
  enqueued: number;
  skipped: number;
  blocked_payload: string[];
  ingest_queued: number;
  batch_id: string | null;
};

export type AdminStreamDatasetSpec = {
  datasetId: string;
  family: string;
  product: string;
  label: string;
};

export type AdminStreamDatasetStatus = {
  datasetId: string;
  status: string;
  subscriptionId: string | null;
  lastMessageAt: string | null;
};

export type AdminStreamProfileRow = {
  profileId: string;
  accountName: string | null;
  subscribedCount: number;
  missingCount: number;
  missingDatasetIds: string[];
  datasets: AdminStreamDatasetStatus[];
};

export type AdminStreamSubscriptionsSummary = {
  datasets: AdminStreamDatasetSpec[];
  items: AdminStreamProfileRow[];
  totals: {
    profiles: number;
    complete: number;
    incomplete: number;
    datasetCount: number;
  };
};

export type AdminStreamSubscribeCounts = {
  created: number;
  reused: number;
  failed: number;
  skipped: number;
};

export type AdminStreamSubscribeResult = {
  totals: AdminStreamSubscribeCounts & { profiles: number };
  results: Array<AdminStreamSubscribeCounts & { profileId: string }>;
};

export type ReportCoverageStage =
  | 'not_scheduled'
  | 'scheduled'
  | 'failed'
  | 'in_s3_not_db'
  | 'in_db';

export type AdminReportCoverageCounts = {
  expected: number;
  ok: number;
  not_scheduled: number;
  scheduled: number;
  failed: number;
  in_s3_not_db: number;
  missing_s3: number;
  missing_db: number;
};

export type AdminReportCoverageQueryCounts = AdminReportCoverageCounts;

export type AdminReportCoverageRow = AdminReportCoverageCounts & {
  profile_id: string;
  account_name: string | null;
  manager_account_name: string | null;
  by_query: Record<string, AdminReportCoverageQueryCounts>;
};

export type AdminReportCoverageWindowCounts = {
  '1h': number;
  '2h': number;
  '4h': number;
  '6h': number;
  '12h': number;
  '24h': number;
};

export type AdminReportCoverageS3Saved = AdminReportCoverageWindowCounts;

export type AdminReportCoverageSummary = {
  range: ReportCoverageRange;
  date_range: { startDate: string; endDate: string };
  query_ids: string[];
  query_labels: Record<string, string>;
  totals: AdminReportCoverageCounts & {
    accounts: number;
    accounts_with_holes: number;
    by_query: Record<string, AdminReportCoverageQueryCounts>;
  };
  s3_saved: AdminReportCoverageWindowCounts;
  failed: AdminReportCoverageWindowCounts;
  items: AdminReportCoverageRow[];
};

export type AdminReportCoverageHole = {
  query_id: string;
  window_start: string;
  window_end: string;
  stage: ReportCoverageStage;
  status: string | null;
  has_s3: boolean;
  has_db: boolean;
  failure_class: string | null;
  failure_reason: string | null;
  amazon_report_id: string | null;
};

export type AdminReportCoverageDetail = AdminReportCoverageCounts & {
  range: ReportCoverageRange;
  date_range: { startDate: string; endDate: string };
  profile_id: string;
  account_name: string | null;
  manager_account_name: string | null;
  query_ids: string[];
  query_labels: Record<string, string>;
  by_query: Record<string, AdminReportCoverageQueryCounts>;
  holes: AdminReportCoverageHole[];
};

export type AdminVendorReportCoverageRow = AdminReportCoverageCounts & {
  credential_id: string;
  display_name: string | null;
  selling_partner_id: string | null;
  by_query: Record<string, AdminReportCoverageQueryCounts>;
};

export type AdminVendorReportCoverageSummary = {
  range: ReportCoverageRange;
  date_range: { startDate: string; endDate: string };
  query_ids: string[];
  query_labels: Record<string, string>;
  totals: AdminReportCoverageCounts & {
    accounts: number;
    accounts_with_holes: number;
    by_query: Record<string, AdminReportCoverageQueryCounts>;
  };
  s3_saved: AdminReportCoverageWindowCounts;
  failed: AdminReportCoverageWindowCounts;
  items: AdminVendorReportCoverageRow[];
};

export type AdminVendorReportCoverageDetail = AdminReportCoverageCounts & {
  range: ReportCoverageRange;
  date_range: { startDate: string; endDate: string };
  credential_id: string;
  display_name: string | null;
  selling_partner_id: string | null;
  query_ids: string[];
  query_labels: Record<string, string>;
  by_query: Record<string, AdminReportCoverageQueryCounts>;
  holes: AdminReportCoverageHole[];
};

export const adminApi = {
  async listUsers(params?: { search?: string; limit?: number; offset?: number }) {
    const { data } = await api.get<
      ApiResponse<{ items: AdminUserListItem[]; total: number }>
    >('/api/v1/admin/users', { params });
    return data;
  },

  async getUser(userId: string) {
    const { data } = await api.get<ApiResponse<AdminUserDetail>>(`/api/v1/admin/users/${userId}`);
    return data;
  },

  async createUser(body: {
    email: string;
    password: string;
    global_role: 'super_admin' | 'account_owner';
    profile_ids?: string[];
    flipkart_accounts?: { credential_id: string; ad_account_id: string }[];
    brand_ids?: string[];
  }) {
    const { data } = await api.post<ApiResponse<AdminUserDetail>>('/api/v1/admin/users', body);
    return data;
  },

  async updateUser(
    userId: string,
    body: {
      email?: string;
      password?: string;
      is_active?: boolean;
      global_role?: 'super_admin' | 'account_owner';
    }
  ) {
    const { data } = await api.patch<ApiResponse<AdminUserDetail>>(
      `/api/v1/admin/users/${userId}`,
      body
    );
    return data;
  },

  async replaceProfileAccess(
    userId: string,
    grants: { profile_id: string; role_code?: string }[],
    flipkartGrants: { credential_id: string; ad_account_id: string; role_code?: string }[] = [],
    brandGrants: { brand_id: string; role_code?: string }[] = []
  ) {
    const { data } = await api.put<ApiResponse<AdminUserDetail>>(
      `/api/v1/admin/users/${userId}/profile-access`,
      { grants, flipkart_grants: flipkartGrants, brand_grants: brandGrants }
    );
    return data;
  },

  async listAssignableProfiles(search?: string) {
    const { data } = await api.get<ApiResponse<AssignableAccount[]>>('/api/v1/admin/profiles', {
      params: search ? { search } : undefined,
    });
    return data;
  },

  async getAccountMetrics(month: string) {
    const { data } = await api.get<ApiResponse<AdminAccountMetricsResponse>>(
      '/api/v1/admin/account-metrics',
      { params: { month } }
    );
    return data;
  },

  async getReportCoverage(params: ReportCoverageParams) {
    const { data } = await api.get<ApiResponse<AdminReportCoverageSummary>>(
      '/api/v1/admin/report-coverage',
      { params: coverageQueryParams(params) }
    );
    return data;
  },

  async getReportCoverageHoles(profileId: string, params: ReportCoverageParams) {
    const { data } = await api.get<ApiResponse<AdminReportCoverageDetail>>(
      `/api/v1/admin/report-coverage/${encodeURIComponent(profileId)}`,
      { params: coverageQueryParams(params) }
    );
    return data;
  },

  async enqueueReportCoverageHoles(profileId: string, body: ReportCoverageEnqueueBody) {
    const { data } = await api.post<ApiResponse<ReportCoverageEnqueueResult>>(
      `/api/v1/admin/report-coverage/${encodeURIComponent(profileId)}/enqueue`,
      body
    );
    return data;
  },

  async getVendorReportCoverage(params: ReportCoverageParams) {
    const { data } = await api.get<ApiResponse<AdminVendorReportCoverageSummary>>(
      '/api/v1/admin/vendor-report-coverage',
      { params: coverageQueryParams(params) }
    );
    return data;
  },

  async getVendorReportCoverageHoles(credentialId: string, params: ReportCoverageParams) {
    const { data } = await api.get<ApiResponse<AdminVendorReportCoverageDetail>>(
      `/api/v1/admin/vendor-report-coverage/${encodeURIComponent(credentialId)}`,
      { params: coverageQueryParams(params) }
    );
    return data;
  },

  async enqueueVendorReportCoverageHoles(credentialId: string, body: ReportCoverageEnqueueBody) {
    const { data } = await api.post<ApiResponse<ReportCoverageEnqueueResult>>(
      `/api/v1/admin/vendor-report-coverage/${encodeURIComponent(credentialId)}/enqueue`,
      body
    );
    return data;
  },

  async getSellerReportCoverage(params: ReportCoverageParams) {
    const { data } = await api.get<ApiResponse<AdminVendorReportCoverageSummary>>(
      '/api/v1/admin/seller-report-coverage',
      { params: coverageQueryParams(params) }
    );
    return data;
  },

  async getSellerReportCoverageHoles(credentialId: string, params: ReportCoverageParams) {
    const { data } = await api.get<ApiResponse<AdminVendorReportCoverageDetail>>(
      `/api/v1/admin/seller-report-coverage/${encodeURIComponent(credentialId)}`,
      { params: coverageQueryParams(params) }
    );
    return data;
  },

  async enqueueSellerReportCoverageHoles(credentialId: string, body: ReportCoverageEnqueueBody) {
    const { data } = await api.post<ApiResponse<ReportCoverageEnqueueResult>>(
      `/api/v1/admin/seller-report-coverage/${encodeURIComponent(credentialId)}/enqueue`,
      body
    );
    return data;
  },

  async getStreamSubscriptions() {
    const { data } = await api.get<ApiResponse<AdminStreamSubscriptionsSummary>>(
      '/api/v1/admin/streams/subscriptions'
    );
    return data;
  },

  async subscribeStreamDatasets(profileId?: string) {
    const { data } = await api.post<ApiResponse<AdminStreamSubscribeResult>>(
      '/api/v1/admin/streams/subscriptions/subscribe',
      profileId ? { profileId } : {}
    );
    return data;
  },
};

function coverageQueryParams(params: ReportCoverageParams) {
  if (params.startDate && params.endDate) {
    return { startDate: params.startDate, endDate: params.endDate };
  }
  return { range: params.range ?? 'lookback' };
}
